import { useQuery } from "@tanstack/react-query";
import { BarChart, Bar, AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import api, { fmtINR } from "@/lib/api";
import { PageHeader } from "@/components/common";

export default function Reports() {
  const { data: sales } = useQuery({ queryKey: ["rep-sales"], queryFn: async () => (await api.get("/admin/reports/sales-by-day?days=30")).data });
  const { data: channels } = useQuery({ queryKey: ["rep-channels"], queryFn: async () => (await api.get("/admin/reports/orders-by-channel")).data });
  const { data: top } = useQuery({ queryKey: ["rep-top"], queryFn: async () => (await api.get("/admin/reports/top-products")).data });
  const { data: agents } = useQuery({ queryKey: ["rep-agents"], queryFn: async () => (await api.get("/admin/reports/agent-performance")).data });

  const tooltipStyle = { background: "#111827", border: "1px solid #1E293B", borderRadius: 8, fontSize: 12 };

  return (
    <div data-testid="reports-page">
      <PageHeader title="Reports" sub="Sales, profit trends, product and agent performance." testid="reports-title" />

      <div className="grid gap-4 lg:grid-cols-2">
        <div className="rounded-xl border border-slate-800 bg-[#111827] p-5" data-testid="report-sales-trend">
          <h3 className="mb-4 text-sm font-semibold text-slate-200">Sales & Profit Trend (30 days)</h3>
          <ResponsiveContainer width="100%" height={240}>
            <AreaChart data={sales || []}>
              <CartesianGrid stroke="#1E293B" strokeDasharray="3 3" />
              <XAxis dataKey="date" tick={{ fill: "#64748B", fontSize: 9 }} tickFormatter={(v) => v.slice(8)} />
              <YAxis tick={{ fill: "#64748B", fontSize: 9 }} />
              <Tooltip contentStyle={tooltipStyle} />
              <Area type="monotone" dataKey="sales" stroke="#FF5722" fill="#FF572233" strokeWidth={2} name="Sales ₹" />
              <Area type="monotone" dataKey="profit" stroke="#10B981" fill="#10B98122" strokeWidth={2} name="Profit ₹" />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        <div className="rounded-xl border border-slate-800 bg-[#111827] p-5" data-testid="report-channel-bars">
          <h3 className="mb-4 text-sm font-semibold text-slate-200">Revenue by Channel</h3>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={channels || []}>
              <CartesianGrid stroke="#1E293B" strokeDasharray="3 3" />
              <XAxis dataKey="channel" tick={{ fill: "#64748B", fontSize: 10 }} />
              <YAxis tick={{ fill: "#64748B", fontSize: 9 }} />
              <Tooltip contentStyle={tooltipStyle} />
              <Bar dataKey="sales" fill="#FF5722" radius={[4, 4, 0, 0]} name="Sales ₹" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="rounded-xl border border-slate-800 bg-[#111827] p-5" data-testid="report-top-products">
          <h3 className="mb-3 text-sm font-semibold text-slate-200">Top Products by Revenue</h3>
          <div className="space-y-1.5">
            {(top || []).map((p, i) => (
              <div key={p.sku} className="flex items-center justify-between rounded-lg bg-slate-900/60 px-3 py-2 text-xs">
                <span className="text-slate-300"><span className="mr-2 font-mono text-orange-400">#{i + 1}</span>{p.name}</span>
                <span className="font-mono text-slate-400">{p.qty} units · <b className="text-slate-100">{fmtINR(p.revenue)}</b></span>
              </div>
            ))}
            {!top?.length && <p className="py-8 text-center text-xs text-slate-500">No data</p>}
          </div>
        </div>

        <div className="rounded-xl border border-slate-800 bg-[#111827] p-5" data-testid="report-agent-performance">
          <h3 className="mb-3 text-sm font-semibold text-slate-200">Agent Leaderboard</h3>
          <div className="space-y-1.5">
            {(agents || []).map((a, i) => (
              <div key={a.agent_id} className="flex items-center justify-between rounded-lg bg-slate-900/60 px-3 py-2 text-xs">
                <span className="text-slate-300"><span className="mr-2 font-mono text-orange-400">#{i + 1}</span>{a.business_name}</span>
                <span className="font-mono text-slate-400">{a.orders} orders · <b className="text-emerald-400">{fmtINR(a.profit)}</b> profit</span>
              </div>
            ))}
            {!agents?.length && <p className="py-8 text-center text-xs text-slate-500">No data</p>}
          </div>
        </div>
      </div>
    </div>
  );
}
